import { I18N_DOMAIN } from '../../../editor/settings';
import { copyURLToClipboard, quizAlert } from '../helpers';
import { validationCheck } from '../validation-check';

const { __ } = wp.i18n;
const { round } = Math;

let shouldUpdateTotalParticipants = true;

/**
 * Posts to the trivia-answers custom endpoint.
 *
 * @param {string} answerId - The trivia answer unique id.
 * @param {string} questionId - The trivia quiz unique id.
 * @param {string} individualQuestionId - The trivia question unique id.
 */
const sendAnswers = async (answerId, questionId, individualQuestionId) =>
	wp
		.apiFetch({
			path: `/mavis/v1/trivia-answers`,
			method: 'POST',
			data: {
				trivia_id: questionId,
				answer_id: answerId,
				question_id: individualQuestionId,
				update_total_participants: shouldUpdateTotalParticipants,
			},
		})
		.catch((err) => console.error(err.message)) // eslint-disable-line
		.then((data) => JSON.parse(data));

/**
 * Posts to the trivia-answers custom endpoint.
 *
 * @param {array} selectedAnswers - Array of selected answer IDs.
 * @param {array} questionIds - Array of question IDs relevant to the answer IDs
 * @param {string} triviaId - The trivia quiz unique id.
 * @param {integer} numberOfQuestions - The number of questions in a trivia quiz
 *
 */
const sendSubmit = async (selectedAnswers, triviaId, questionIds, numberOfQuestions) =>
	wp
		.apiFetch({
			path: `/mavis/v1/trivia-results`,
			method: 'POST',
			data: {
				selected_answers: selectedAnswers,
				trivia_id: triviaId,
				question_ids: questionIds,
				number_of_questions: numberOfQuestions,
			},
		})
		.catch((err) => console.error(err.message)) // eslint-disable-line
		.then((data) => JSON.parse(data));

/**
 * Checks all questions are answered.
 *
 * @param {array} trivia - Array of trivia quizzes on a page/post
 *
 */
const allQuestionsAnswered = (trivia, clickedQuestionId) => {
	let allAnswered = true;
	const triviaQuestions = trivia.querySelectorAll('.wp-block-mavis-trivia-question');

	triviaQuestions.forEach((triviaQuestion) => {
		const { questionAnswered, questionId } = triviaQuestion.dataset;

		if (questionId !== clickedQuestionId && questionAnswered === '0') {
			allAnswered = false;
		}
	});

	return allAnswered;
};

/**
 * Sends ids to the sendAnswers function and handles the api response.
 *
 * @param {string} answerId - The trivia answer unique id.
 * @param {string} questionId - The trivia quiz unique id.
 * @param {string} individualQuestionId - The trivia question unique id.
 */
const handleResponse = async (answerId, questionId, individualQuestionId) => {
	// Send id's to endpoint.
	const response = await sendAnswers(answerId, questionId, individualQuestionId);

	const { isCorrect, correctAnswerId, factoid } = response;

	// get users answer by id
	const answer = document.getElementById(answerId);

	// if true is returned, user was correct and is-correct class is added, else is-incorrect class is added
	if (isCorrect === true) {
		answer.parentElement.classList.add('is-correct');
	} else {
		answer.parentElement.classList.add('is-incorrect');
	}

	// Get the closest question to the selected answer
	const individualQuestion = document.querySelector(`[data-question-id="${individualQuestionId}"]`);

	// Updates question-answered data attribute
	individualQuestion.setAttribute('data-question-answered', '1');

	if (window.dataLayer) {
		// Push interaction to GA
		window.dataLayer.push({
			event: `triviaQuizInteraction`,
			interactionType: `answerSelect`,
			quizName: document.querySelector('.wp-block-post-title').innerText,
			questionNumber: individualQuestion.getAttribute('data-question-number'),
			questionText: individualQuestion.querySelector('h2').innerText,
			answerChosenText: answer.getAttribute('title'),
			answerChosenLetter: answer.getAttribute('data-answer-letter'),
			answerResult: isCorrect ? 'correct' : 'incorrect',
		});
	}

	// if correct answer id is NOT the same as user answer id,
	if (correctAnswerId !== answerId) {
		// Get the correct answer
		const correctAnswer = document.getElementById(correctAnswerId);

		// Get the empty answer status.
		const answerStatus = individualQuestion.querySelector('.mavisTrivia-answer-status');

		// Output WRONG status along with the correct answer for the question
		answerStatus.innerHTML =
			__('Wrong: answer ', 'mavis') +
			correctAnswer.dataset.answerLetter +
			__(' is correct', 'mavis');
	} else {
		// Get the correct answer
		const correctAnswer = document.getElementById(correctAnswerId);

		// Get the empty answer status element
		const answerStatus = individualQuestion.querySelector('.mavisTrivia-answer-status');

		// Output CORRECT status along with the correct answer for the question
		answerStatus.innerHTML = __('Correct: answer ', 'mavis') + correctAnswer.dataset.answerLetter;
	}

	// Populate the factoid element if there is a valid factoid.
	if (factoid) {
		const factoidEl = individualQuestion.querySelector('.mavisTrivia-factoid');
		factoidEl.innerHTML = factoid;
	}

	const postAnswerContent = individualQuestion.querySelector('.mavisTrivia-postAnswerContent');
	postAnswerContent.classList.remove('is-hidden');

	shouldUpdateTotalParticipants = false;
};

/**
 * Resets the quiz so the user can play again.
 * @returns {void}
 */
const handlePlayAgain = () => {
	// Get elements we need to reset.
	const triviaForm = document.querySelector('form.wp-block-mavis-trivia');
	const answerInputs = [...document.querySelectorAll('.wp-block-mavis-trivia-answer input')];
	const correctAnswerElements = [
		...document.querySelectorAll('.wp-block-mavis-trivia-answer.is-correct'),
	];
	const incorrectAnswerElements = [
		...document.querySelectorAll('.wp-block-mavis-trivia-answer.is-incorrect'),
	];
	const results = document.querySelector('.mavisTrivia-results');
	const submitArea = document.querySelector('.mavisTrivia-submit-area');
	const postTitle = document.querySelector('.wp-block-post-title');
	const factoids = document.querySelectorAll('.mavisTrivia-factoid');
	const statuses = document.querySelectorAll('.mavisTrivia-answer-status');
	const postAnswerContents = document.querySelectorAll('.mavisTrivia-postAnswerContent');
	const questions = document.querySelectorAll('.wp-block-mavis-trivia-question');

	// Reset all inputs back to original state.
	answerInputs.forEach((input) => {
		input.checked = false; // eslint-disable-line no-param-reassign
		input.removeAttribute('disabled');
	});
	correctAnswerElements.forEach((answer) => answer.classList.remove('is-correct'));
	incorrectAnswerElements.forEach((answer) => answer.classList.remove('is-incorrect'));
	submitArea.classList.remove('is-inactive');

	// Hide factoids.
	factoids.forEach((factoid) => {
		factoid.innerHTML = ''; // eslint-disable-line no-param-reassign
		factoid.classList.remove('is-active');
	});

	// Hide statuses.
	statuses.forEach((status) => status.classList.remove('has-answered'));
	statuses.forEach((status) => {
		status.innerHTML = ''; // eslint-disable-line no-param-reassign
	});

	// Hide the post answer content for each question.
	postAnswerContents.forEach((content) => content.classList.add('is-hidden'));

	// Reset questions.
	questions.forEach((question) => question.setAttribute('data-question-answered', '0'));

	// Hide results and remove the output we built to show the score.
	results.classList.remove('is-active');
	results.replaceChildren();

	// Reset the form.
	triviaForm.reset();

	if (window.dataLayer) {
		// Push retake to GA.
		window.dataLayer.push({
			event: 'triviaQuizInteraction',
			interactionType: 'retake',
			quizName: document.querySelector('.wp-block-post-title').innerText,
		});
	}

	// Scroll back to top of post.
	postTitle.scrollIntoView();
};

/**
 * Handles form submit, sends all selected answers to a custom endpoint
 *
 * @param {array} selectedAnswers - Array of all the selected answers in the trivia quiz
 * @param {array} questionIds - Array of question IDs relevant to the answer IDs
 * @param {string} triviaId - The unique trivia quiz ID
 * @param {integer} numberOfQuestions - The number of questions in a trivia quiz
 *
 */
const handleSubmit = async (selectedAnswers, triviaId, questionIds, numberOfQuestions) => {
	const response = await sendSubmit(selectedAnswers, triviaId, questionIds, numberOfQuestions);

	if (response) {
		const { score, noOfQuestions, profileText, profileImage } = response;

		// Get the current trivia block
		const trivia = document.querySelector(`[data-question-id="${triviaId}`);

		if (window.dataLayer) {
			// Push submit to GA.
			window.dataLayer.push({
				event: 'triviaQuizComplete',
				quizName: document.querySelector('.wp-block-post-title').innerText,
				quizScore: `${score}/${noOfQuestions}`,
			});
		}

		// Get all questions for the trivia quiz
		const triviaQuestions = trivia.querySelectorAll('.wp-block-mavis-trivia-question');

		// Loop over each question and if it has the class is-unanswered remove it
		triviaQuestions.forEach((element) => {
			if (element.classList.contains('is-unanswered')) {
				element.classList.remove('is-unanswered');
			}
		});

		// Find the results element
		const results = trivia.querySelector('.mavisTrivia-results');
		const scorePercentage = round((score / noOfQuestions) * 100);

		if (scorePercentage < 33.33) {
			results.classList.add('is-bad-result');
		} else if (scorePercentage >= 33.33 && scorePercentage < 66.66) {
			results.classList.add('is-in-between-result');
		} else if (scorePercentage >= 66.66) {
			results.classList.add('is-good-result');
		}

		// Build the output for the all the results elements
		const output = `
		<div class="mavisTrivia-resultsHeader">
			${profileImage}
    	<span class="mavisTrivia-resultsHeader-container">
				<span class="mavisTrivia-resultsHeader-prefix">
					${__('You scored:', I18N_DOMAIN)}
				</span>
				<span class="mavisTrivia-resultsHeader-name">
					${score}/${noOfQuestions}
				</span>
			</span>
  	</div>
  	<div class="mavisTrivia-resultsContent">
    	<span class="mavisTrivia-resultsText">${profileText}</span>
			<span class="mavisTrivia_resultsContent-question">
				<strong>
					${__('What do you want to do now?', 'mavis')}
				</strong>
			</span>
			<div class="mavisTrivia_resultsContent-buttons" role="button">
				<div class="wp-block-button">
					<button class="wp-block-button__link wp-element-button has-mavis-color-trivia-secondary-background-color play-again">
						${__('Play again', 'mavis')}
					</button>
				</div>
				<div class="wp-block-button">
					<button class="wp-block-button__link wp-element-button has-mavis-color-trivia-secondary-background-color share-with-friends">
						${__('Share with friends', 'mavis')}
					</button>
				</div>
			</div>
			</div>
			${quizAlert}`;

		// Insert the rest of the output elements AFTER the image
		results.insertAdjacentHTML('beforeend', output);

		// add class to display the results
		results.classList.add('is-active');

		document
			.querySelector('button.wp-block-button__link.share-with-friends')
			.addEventListener('click', (event) => {
				copyURLToClipboard(event);
				if (window.dataLayer) {
					// Push share to GA.
					window.dataLayer.push({
						event: 'triviaQuizInteraction',
						interactionType: 'share',
						quizName: document.querySelector('.wp-block-post-title').innerText,
					});
				}
			});

		document
			.querySelector('button.wp-block-button__link.play-again')
			.addEventListener('click', handlePlayAgain);
	}
};

/**
 * Checks if the amount of selected items, if all are selected remove class from button
 *
 * @param {array} trivia - Array of trivia quizzes on a page/post
 *
 */
const submitButtonValidation = (trivia, questionId) => {
	// If the number of disabled answers is equal to the number of questions
	if (allQuestionsAnswered(trivia, questionId)) {
		// Get validation warning
		const validationWarning = trivia.querySelector('.mavisTrivia-validation-warning');

		// Check if it exists on the page
		if (validationWarning) {
			// If it does, remove the element all together
			validationWarning.classList.remove('is-active');
			validationWarning.innerHTML = '';
		}

		// Get submit button
		const submitButton = trivia.querySelector('.mavisTrivia-submit-area .wp-block-button button');
		// And remove the class that disables the button
		if (submitButton.classList.contains('missing-answers')) {
			submitButton.classList.remove('missing-answers');
		}
	}
};

window.addEventListener('DOMContentLoaded', () => {
	// Get all trivia blocks
	const triviaBlocks = document.querySelectorAll('.wp-block-mavis-trivia');

	triviaBlocks.forEach((trivia) => {
		const { questionId } = trivia.dataset;

		// Add click listener for each trivia block
		trivia.addEventListener('click', (event) => {
			// if click is on a trivia answer input
			if (
				event.target.tagName === 'INPUT' &&
				event.target.parentElement.classList.contains('wp-block-mavis-trivia-answer')
			) {
				const answerId = event.target.id;
				const individualQuestion = event.target.closest('.wp-block-mavis-trivia-question');

				const disableAnswers = individualQuestion.querySelectorAll(
					'.wp-block-mavis-trivia-answer input',
				);

				disableAnswers.forEach((element) => {
					if (element.id !== answerId) {
						element.setAttribute('disabled', '');
					}
				});

				// On click check if question has the unanswered class applied and if so, remove it
				if (individualQuestion.classList.contains('is-unanswered')) {
					individualQuestion.classList.remove('is-unanswered');
				}

				handleResponse(answerId, questionId, individualQuestion.dataset.questionId);
				submitButtonValidation(trivia, individualQuestion.dataset.questionId);

				// If all questions are not answered, run the validation check
				if (!allQuestionsAnswered(trivia)) {
					validationCheck(trivia, event, 'INPUT', 'trivia');
				}
			}

			// if click is on the submit button
			if (
				event.target.tagName === 'BUTTON' &&
				event.target.parentElement.parentElement.classList.contains('mavisTrivia-submit-area')
			) {
				event.preventDefault();

				// const triviaId = event.target.parentElement.parentElement.dataset.questionId;
				const triviaId = event.target.closest('.wp-block-mavis-trivia').dataset.questionId;

				// Get the number of questions in a quiz
				const numberOfQuestions = trivia.querySelectorAll('.wp-block-mavis-trivia-question');

				const questionIds = [];

				// Filter inputs for checked inputs only, and grab their ids
				const selectedOptions = [...trivia.getElementsByTagName('input')]
					.filter((input) => input.checked)
					.map((input) => input.id);

				// If all questions are not answered, run the validation check
				if (!allQuestionsAnswered(trivia)) {
					validationCheck(trivia, event, 'A', 'trivia');
					return;
				}

				// If all questions have been answered and submitted grab submit area
				const submitArea = trivia.querySelector('.mavisTrivia-submit-area');

				// and remove the submit area
				if (submitArea) {
					submitArea.classList.add('is-inactive');
				}

				selectedOptions.forEach((option) => {
					questionIds.push(
						document.getElementById(option).closest('.wp-block-mavis-trivia-question').dataset
							.questionId,
					);
				});

				handleSubmit(selectedOptions, triviaId, questionIds, numberOfQuestions.length);
			}
		});
	});
});
